import * as React from "react";
import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export function ProductGallery({ images }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [thumbnailStartIndex, setThumbnailStartIndex] = useState(0);

  // Responsive thumbnail counts
  const getThumbnailCount = () => {
    if (typeof window === "undefined") return 5;
    const width = window.innerWidth;
    if (width < 480) return 3; // Mobile
    if (width < 640) return 4; // Small mobile
    return 5; // Default for larger screens
  };

  const [thumbnailsToShow, setThumbnailsToShow] = useState(getThumbnailCount);

  useEffect(() => {
    const handleResize = () => {
      setThumbnailsToShow(getThumbnailCount());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handlePrevious = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleThumbnailClick = (globalIndex) => {
    setCurrentImageIndex(globalIndex);
  };

  // Update thumbnail window when current image changes
  useEffect(() => {
    if (currentImageIndex < thumbnailStartIndex) {
      setThumbnailStartIndex(currentImageIndex);
    } else if (currentImageIndex >= thumbnailStartIndex + thumbnailsToShow) {
      setThumbnailStartIndex(currentImageIndex - thumbnailsToShow + 1);
    }
  }, [thumbnailStartIndex, currentImageIndex, thumbnailsToShow]);

  const handleThumbnailsScroll = (direction) => {
    const maxStartIndex = Math.max(0, images.length - thumbnailsToShow);

    setThumbnailStartIndex((prev) => {
      if (direction === "left") {
        // Ensure we don't go below 0
        return Math.max(0, prev - 1);
      } else {
        // Ensure we don't exceed maxStartIndex
        return Math.min(maxStartIndex, prev + 1);
      }
    });
  };

  if (!images?.length) return null;

  const showLeftScroll = thumbnailStartIndex > 0;
  const showRightScroll =
    thumbnailStartIndex < images.length - thumbnailsToShow;

  return (
    <div className="space-y-3 sm:space-y-4 flex flex-col items-center w-full">
      {/* Main Image */}
      <div className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group w-full max-w-[400px] sm:max-w-[500px] md:max-w-[600px]">
        <img
          src={images[currentImageIndex]}
          alt="Product main"
          className="w-full h-full object-cover"
          loading="lazy"
        />

        {/* Navigation buttons - positioned inside the image container */}
        <button
          onClick={handlePrevious}
          className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 sm:p-2.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-md hidden sm:block"
          aria-label="Previous image"
        >
          <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6 text-gray-800" />
        </button>

        <button
          onClick={handleNext}
          className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 sm:p-2.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-md hidden sm:block"
          aria-label="Next image"
        >
          <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6 text-gray-800" />
        </button>

        {/* Mobile navigation dots */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 sm:hidden">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                currentImageIndex === index ? "bg-white" : "bg-white/50"
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Thumbnails - with proper container management */}
      <div className="w-full max-w-[400px] sm:max-w-[500px] md:max-w-[600px] px-8 sm:px-10">
        <div className="relative flex items-center w-full">
          {showLeftScroll && (
            <button
              onClick={() => handleThumbnailsScroll("left")}
              className="absolute -left-6 sm:-left-8 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-100 rounded-full p-1 sm:p-1.5 shadow-md z-10 border"
              aria-label="Scroll thumbnails left"
            >
              <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4 text-gray-800" />
            </button>
          )}

          <div
            className={`grid gap-1.5 sm:gap-2 w-full`}
            style={{ gridTemplateColumns: `repeat(${thumbnailsToShow}, 1fr)` }}
          >
            {images
              .slice(
                thumbnailStartIndex,
                thumbnailStartIndex + thumbnailsToShow
              )
              .map((image, index) => {
                const globalIndex = thumbnailStartIndex + index;
                return (
                  <button
                    key={globalIndex}
                    onClick={() => handleThumbnailClick(globalIndex)}
                    className={`aspect-square bg-gray-100 rounded-lg overflow-hidden hover:opacity-90 transition-all duration-200
                    ${
                      currentImageIndex === globalIndex
                        ? "ring-2 ring-blue-500 opacity-100"
                        : "opacity-70"
                    }`}
                  >
                    <img
                      src={image}
                      alt={`Product thumbnail ${globalIndex + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </button>
                );
              })}
          </div>

          {showRightScroll && (
            <button
              onClick={() => handleThumbnailsScroll("right")}
              className="absolute -right-6 sm:-right-8 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-100 rounded-full p-1 sm:p-1.5 shadow-md z-10 border"
              aria-label="Scroll thumbnails right"
            >
              <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 text-gray-800" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProductGallery;
